import { Injectable } from '@angular/core'
import { BehaviorSubject, Subject } from 'rxjs'

/**
 * Lo que la pantalla de Cobranza comparte con sus vistas: **el mes**.
 *
 * Las vistas dejaron de ser hijas de la plantilla para pasar a ser hijas de la RUTA —así el
 * encabezado y el embudo sobreviven al cambio de vista en vez de recrearse enteros—, y un componente
 * enrutado no recibe `@Input` de su padre. Este servicio es ese cable.
 *
 * `providedIn: 'root'` y no en el componente: un hijo de `<router-outlet>` se crea en el inyector
 * del outlet, y colgar el servicio del componente padre funciona pero depende de un detalle de la
 * jerarquía de inyectores que no es evidente al leerlo. A cambio hay que **reiniciar el mes al
 * entrar**, que es lo que hace `reset()`.
 */
@Injectable({ providedIn: 'root' })
export class CobranzaShellService {
	private readonly month$$ = new BehaviorSubject<Date>(new Date())

	/** El mes que gobierna el embudo y el resumen de Comprobantes. */
	readonly month$ = this.month$$.asObservable()

	private readonly refresh$$ = new Subject<void>()

	/**
	 * Una vista acaba de cambiar el dinero o los comprobantes del mes, y el embudo tiene que enterarse.
	 *
	 * Desde que el embudo vive en el cascarón —para no recrearse al cambiar de vista— ya no se recarga
	 * solo: hay que decírselo. Cargar cuatro facturas y ver «Facturado $0.00» arriba es peor que no
	 * tener la cifra, porque la que se ve es falsa.
	 */
	readonly refresh$ = this.refresh$$.asObservable()

	get month(): Date {
		return this.month$$.value
	}

	/** Lo llama quien acaba de escribir: la carga de facturas, el asistente de pagos, un ajuste. */
	refresh(): void {
		this.refresh$$.next()
	}

	setMonth(value: Date): void {
		this.month$$.next(value || new Date())
	}

	/**
	 * Vuelve al mes en curso. Se llama al montar la pantalla.
	 *
	 * Sin esto, salir de Cobranza en julio y volver una hora después dejaría el embudo en julio sin
	 * que nadie lo hubiera pedido: al ser un servicio de raíz, el mes sobrevive a la navegación.
	 */
	reset(): void {
		this.month$$.next(new Date())
	}
}
